import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import {
  creatorProfileSelector,
  creatorProfileErrorSelector,
  creatorProfileRollSelector,
  creatorProfileRollErrorSelector,
  creatorProfileRollPendingSelector,
  creatorProfileRollPageSelector,
  creatorProfileRollPreviewsAssetsSelector,
  creatorProfileRollIsMoreSelector,
  creatorProfilePendingSelector,
} from './store/creators.selectors';
import {
  getCreatorRoll,
  getProfile,
  pageCreatorRoll,
} from './store/creators.actions';
import { cancelSubscriptions } from 'app/auth/store/auth.actions';
import {
  accessTokenSelector,
  cancelSubscriptionPending,
  sessionSelector,
  userSelector,
} from 'app/auth/store/auth.selectors';

import {
  getConversation,
  getUnreadConversationMessages,
} from 'app/messages/store/message.actions';
import { RoutesList } from 'enums/routes-list.enum';
import { conversationSelector } from 'app/messages/store/messages.selectors';
import { SCREEN_SIZE_BORDER } from 'constants/frontend-contsants';
import { removeNewMessageCounter } from 'app/messages/store/messages.slice';

import Grid from '@mui/material/Grid';
import AppHeader from 'components/app-header';
import AppBackdrop from 'components/app-backdrop';
import ModalConfirm from 'components/modal-confirm';
import ProfileMenu from './components/profile-menu';
import PreviewAssets from 'components/preview-assets';
import ProfileHeader from './components/profile-header';
import ModalSubscribe from 'components/modal-subscribe';
import useWindowDimensions from 'hooks/use-window-dimensions';

import SignInModal from 'components/sign-in-modal';
import CopySnackbar from 'components/copy-snackbar';
import StayTunedPage from 'components/stay-tuned-page';
import PublicAppBar from './components/public-app-bar';
import MarqueeTextBar from './components/marquee-text-bar';
import AssetsList from './components/assets-list/assets-list';
import UnablePaymentModal from 'components/unable-payment-modal';
import CreatorProfileAside from './components/creator-profile-aside';
import CreatorProfileInfoMobile from './components/creator-profile-info-mobile';
import CreatorProfileInfoDesktop from './components/creator-profile-info-dekstop';

import { cancelPending } from 'app/auth/store/auth.slice';
import { useTranslation } from 'react-i18next';
import { NAVIGATE_ROUTE } from 'constants/local-storage';
import { CreatorContentContainer } from 'components/styled/creator-content-container';
import { cleanProfileRoll } from './store/creators.slice';
import { NavigateMessagesHandlerProps } from 'app/messages/types/navigate-messages-handler.props';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK as string);

// TODO rewrte progress to skeletons
const ProfilePage: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { state } = useLocation();
  const { width } = useWindowDimensions();
  const params = useParams<{ username: string }>();
  const access_token = useSelector(accessTokenSelector);
  const session = useSelector(sessionSelector);

  const profile = useSelector(creatorProfileSelector);
  const conversation = useSelector(conversationSelector);
  const error_profile = useSelector(creatorProfileErrorSelector);
  const pending_profile = useSelector(creatorProfilePendingSelector);
  const pending_cancel_sub = useSelector(cancelSubscriptionPending);

  const user = useSelector(userSelector);
  const roll = useSelector(creatorProfileRollSelector);
  const pending_roll = useSelector(creatorProfileRollPendingSelector);
  const error_roll = useSelector(creatorProfileRollErrorSelector);
  const roll_previews_assets = useSelector(
    creatorProfileRollPreviewsAssetsSelector
  );
  const page = useSelector(creatorProfileRollPageSelector);
  const isMore = useSelector(creatorProfileRollIsMoreSelector);

  const [preview, setPreview] = useState({
    opened: false,
    id: '',
    rollId: '',
    contentId: '',
    index: 0,
  });
  const [menuOpened, setMenuOpened] = useState(false);
  const [copyOpened, setCopyOpened] = useState(false);
  const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
  const [unsubscribeConfirmOpened, setUnsubscribeConfirmOpened] =
    useState(false);
  const [opened_subscribe, setOpenedSubscribe] = useState(false);
  const [paymentErrorOpened, setPaymentErrorOpened] = useState(false);
  const [authOpened, setAuthOpened] = useState(false);

  const profileConversation = profile?.conversation;
  const isPublic = !session;
  const isError = error_profile || error_roll;

  const isToShowUnsubscribedUI = useMemo(() => {
    return Boolean(
      !pending_profile &&
        (!profile?.isSubscribed || profile?.subscription?.periodEnd) &&
        !isError
    );
  }, [pending_profile, profile, isError]);

  const isToShowSubscribedUI = useMemo(() => {
    return (
      Boolean(profile?.isSubscribed && !profile?.subscription?.periodEnd) &&
      !isError
    );
  }, [profile, isError]);

  const isConversationEnabled = useMemo(() => {
    return Boolean(
      profile?.conversation.messaging.isEnabled &&
        profile?.conversation.messaging.isActive &&
        !isError
    );
  }, [profile, isError]);

  const isFreeMessages = useMemo(() => {
    return (
      profile?.conversation.messaging.isFree &&
      profile?.conversation.messaging.isActive &&
      profile?.conversation.messaging.isEnabled &&
      isToShowSubscribedUI
    );
  }, [isToShowSubscribedUI, profile]);

  const navigateCreators = () => navigate(RoutesList.Creators_Default);
  const navigateMessageCreator = useCallback(
    ({ conversationId, creatorId }: NavigateMessagesHandlerProps) => {
      if (isPublic) {
        setAuthOpened(true);
        return;
      }
      const route = conversationId
        ? RoutesList.Messages_Creator_Default.replace(
            ':conversation_id',
            conversationId
          )
        : RoutesList.Messages_Creator_New;
      navigate(route, {
        state: {
          isFromProfile: true,
          creatorId: creatorId,
        },
      });
    },
    [navigate, isPublic]
  );
  const handleOpenSubscribe = useCallback(
    () => (isPublic ? setAuthOpened(true) : setOpenedSubscribe(true)),
    [isPublic]
  );

  const handleCloseSubscribe = () => setOpenedSubscribe(false);
  const handleCloseAuth = () => setAuthOpened(false);

  const handleSubscribeSuccess = () => {
    window.location.reload();
  };
  const handlePaymentErrorOpen = () => {
    setPaymentErrorOpened(true);
  };
  const handlePaymentErrorClosed = () => {
    setPaymentErrorOpened(false);
  };

  const handleMenuOpened = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorMenu(event.currentTarget);
      setMenuOpened(true);
    },
    []
  );

  const handleMenuClosed = () => {
    setAnchorMenu(null);
    setMenuOpened(false);
  };

  const handlePreviewOpen = useCallback(
    (asset: any, index: number) => () => {
      if (index > 2 && !profile?.isSubscribed) {
        handleOpenSubscribe();
        return;
      }
      setPreview({
        opened: true,
        id: asset.id,
        rollId: asset.rollId,
        contentId: asset.contentId,
        index,
      });
    },
    [handleOpenSubscribe, profile]
  );

  const handlePreviewClose = () =>
    setPreview({ opened: false, id: '', rollId: '', contentId: '', index: 0 });

  const handleShare = useCallback(async () => {
    if (!profile) {
      return;
    }
    await navigator.clipboard.writeText(profile.url);
    setCopyOpened(true);
  }, [profile]);

  const handleCopyClosed = () => setCopyOpened(false);

  const handleUnsubscribe = useCallback(() => {
    handleMenuClosed();
    setUnsubscribeConfirmOpened(true);
  }, []);

  const handleUnsubscribeConfirmCanceled = () =>
    setUnsubscribeConfirmOpened(false);

  const handleUnsubscribeConfirmed = useCallback(async () => {
    if (!access_token || !profile) {
      return;
    }

    const { error } = await dispatch<any>(
      cancelSubscriptions({
        user_id: access_token.sub.userId,
        roll_id: profile.roll.id,
      })
    );
    setUnsubscribeConfirmOpened(false);

    if (error) {
      return;
    }

    navigate(RoutesList.Creators_Default);
  }, [access_token, profile, navigate, dispatch]);

  const onScroll = useCallback(() => {
    if (!profile || pending_roll || !isMore) {
      return;
    }

    dispatch<any>(
      pageCreatorRoll({ roll_id: profile.roll.id, page: page + 1, size: 12 })
    );
  }, [dispatch, isMore, page, pending_roll, profile]);

  useEffect(() => {
    dispatch<any>(
      getProfile({
        creator_id: state?.creator_id,
        username: params.username as string,
        isPublic: isPublic,
      })
    ).then(({ error, payload }: any) => {
      if (error) {
        return;
      }

      if (!payload?.roll?.id) {
        return;
      }

      return dispatch<any>(
        getCreatorRoll({
          roll_id: payload.roll.id,
          page: 1,
          size: 12,
          isPublic: isPublic,
        })
      );
    });
    return () => {
      dispatch(cleanProfileRoll());
      dispatch(cancelPending());
    };
  }, [state, params.username, dispatch, isPublic]);

  useEffect(() => {
    if (!session || !profileConversation?.id) {
      return;
    }

    dispatch<any>(
      getConversation({
        conversation_id: profileConversation.id,
        fb_token: session.fbToken,
      })
    );
  }, [session, profileConversation, dispatch]);

  useEffect(() => {
    if (!conversation || !session) {
      dispatch(removeNewMessageCounter());
      return;
    }

    if (conversation.lastSenderId === conversation.creatorId) {
      dispatch<any>(
        getUnreadConversationMessages({
          conversation_id: conversation.conversationId,
          fb_token: session.fbToken,
        })
      );
    }
  }, [conversation, dispatch, session]);

  if (pending_profile) {
    return (
      <>
        <PublicAppBar />
        <CreatorContentContainer>
          <ProfileHeader
            isToShowUnsubscribedUI={isToShowUnsubscribedUI}
            isToShowSubscribedUI={isToShowSubscribedUI}
            isPublic={isPublic}
            onClose={navigateCreators}
            onMenuClick={handleMenuOpened}
            onShare={handleShare}
          />
          <AppBackdrop />
        </CreatorContentContainer>
        <Grid
          display={{ xs: 'none', md: 'flex' }}
          sx={{ flexBasis: '400px' }}
        ></Grid>
      </>
    );
  }

  if (pending_cancel_sub) {
    return (
      <Grid
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <AppBackdrop />
      </Grid>
    );
  }

  if (error_profile || error_roll) {
    return <StayTunedPage onNavigateBack={navigateCreators} user={user} />;
  }

  if (!profile) {
    return null;
  }

  if (user && !user.username) {
    localStorage.setItem(NAVIGATE_ROUTE, `/${params.username}`);
    return <Navigate to={RoutesList.Onboarding_Default} />;
  }

  return (
    <>
      {isPublic ? (
        <PublicAppBar />
      ) : (
        <AppHeader user={user} display={{ xs: 'none', md: 'flex' }} />
      )}

      <CreatorContentContainer>
        <ProfileHeader
          isToShowUnsubscribedUI={isToShowUnsubscribedUI}
          isToShowSubscribedUI={isToShowSubscribedUI}
          isPublic={isPublic}
          onClose={navigateCreators}
          onMenuClick={handleMenuOpened}
          onShare={handleShare}
        />

        {width <= SCREEN_SIZE_BORDER && (
          <CreatorProfileInfoMobile
            isToShowSubscribedUI={isToShowSubscribedUI}
            isToShowUnsubscribedUI={isToShowUnsubscribedUI}
            handleOpenSubscribe={handleOpenSubscribe}
            isConversationEnabled={isConversationEnabled}
            navigateMessageCreator={navigateMessageCreator}
          />
        )}

        {width > SCREEN_SIZE_BORDER && (
          <CreatorProfileInfoDesktop
            isToShowSubscribedUI={isToShowSubscribedUI}
            isToShowUnsubscribedUI={isToShowUnsubscribedUI}
            handleOpenSubscribe={handleOpenSubscribe}
            isConversationEnabled={isConversationEnabled}
            navigateMessageCreator={navigateMessageCreator}
          />
        )}

        {isFreeMessages && (
          <MarqueeTextBar pinkText="FREE" text="MESSAGING UNLOCKED!!!" />
        )}

        <AssetsList
          onScroll={onScroll}
          handlePreviewOpen={handlePreviewOpen}
          isConversationEnabled={isConversationEnabled}
        />
        {pending_roll && <AppBackdrop />}
      </CreatorContentContainer>

      <CreatorProfileAside
        isConversationEnabled={isConversationEnabled}
        handleOpenSubscribe={handleOpenSubscribe}
        isToShowUnsubscribedUI={isToShowUnsubscribedUI}
        navigateMessageCreator={navigateMessageCreator}
      />

      <PreviewAssets
        opened={preview.opened}
        index={preview.index}
        assets={roll_previews_assets}
        onClose={handlePreviewClose}
        isSubscribed={roll?.isSubscribed}
        isPublic={isPublic}
        handleOpenSubscribe={handleOpenSubscribe}
      />

      <ProfileMenu
        anchor={anchorMenu}
        opened={menuOpened}
        onShare={handleShare}
        onUnsubscribe={handleUnsubscribe}
        onClose={handleMenuClosed}
      />

      {isPublic && (
        <SignInModal
          onClose={handleCloseAuth}
          open={authOpened}
          username={profile.username}
        />
      )}

      <ModalConfirm
        open={unsubscribeConfirmOpened}
        title={t('titles.unsubscribe')}
        content={t('texts.unsubscribe-warning')}
        confirmText={t('buttons.unsubscribe')}
        cancelText={t('buttons.cancel')}
        onCancel={handleUnsubscribeConfirmCanceled}
        onConfirm={handleUnsubscribeConfirmed}
      />

      {paymentErrorOpened && (
        <UnablePaymentModal
          open={paymentErrorOpened}
          onCancel={handlePaymentErrorClosed}
        />
      )}

      <Elements stripe={stripePromise}>
        <ModalSubscribe
          open={opened_subscribe}
          profile={profile}
          onClose={handleCloseSubscribe}
          onErrorPayment={handlePaymentErrorOpen}
          onSuccess={handleSubscribeSuccess}
        />
      </Elements>
      <CopySnackbar open={copyOpened} onClose={handleCopyClosed} />
    </>
  );
};

export default ProfilePage;
